import { serverDate } from '../../../shared/helpers/dates';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { InvoicesService } from '../../../shared/services/invoices.service';
import { RentalsService } from '../../../shared/services/rentals.service';
import {
  RENTAL_STATUS_META,
  Rental,
} from '../../../shared/types/rental.types';

@Component({
  selector: 'app-rental-detail',
  imports: [
    CommonModule,
    RouterModule,
    LocalizedDatePipe,
    DecimalPipe,
    PageHeaderComponent,
    DataStateComponent,
    ConfirmModalComponent,
    UserCellComponent,
  ],
  templateUrl: './rental-detail.component.html',
})
export class RentalDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly rentalsService = inject(RentalsService);
  private readonly invoicesService = inject(InvoicesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly rental = signal<Rental | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  /** Companion invoice — looked up by rentalId once the rental is loaded. */
  readonly invoiceId = signal<number | null>(null);
  readonly invoiceLoading = signal(false);
  /** True once we've attempted the lookup (regardless of outcome). */
  readonly invoiceFetched = signal(false);

  // delete modal
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  readonly statusMeta = RENTAL_STATUS_META;

  readonly vehicleName = computed(() => {
    const v = this.rental()?.vehicle;
    if (!v) return '—';
    const make = v.make?.entityCode ?? v.make?.name ?? '';
    const model = v.model?.entityCode ?? v.model?.name ?? '';
    return [make, model].filter(Boolean).join(' ') || `Véhicule #${v.id}`;
  });

  /** Computed duration in days from startDate → endDate. */
  readonly durationDays = computed(() => {
    const r = this.rental();
    if (!r?.startDate || !r?.endDate) return null;
    const start = serverDate(r.startDate).getTime();
    const end = serverDate(r.endDate).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return null;
    const ms = end - start;
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  });

  /** True if the rental was returned later than the planned end date. */
  readonly returnedLate = computed(() => {
    const r = this.rental();
    if (!r?.actualReturnDate || !r?.endDate) return false;
    return serverDate(r.actualReturnDate).getTime() > serverDate(r.endDate).getTime();
  });

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.rentalsService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.rental.set(r);
          this.loading.set(false);
          this.fetchInvoiceLookup();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cette location.'),
          );
        },
      });
  }

  /** Best-effort companion invoice lookup. A 404 just means "no invoice yet". */
  private fetchInvoiceLookup(): void {
    this.invoiceLoading.set(true);
    this.invoiceFetched.set(false);
    this.invoicesService
      .getByRental(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inv) => {
          this.invoiceId.set(inv.id);
          this.invoiceLoading.set(false);
          this.invoiceFetched.set(true);
        },
        error: () => {
          this.invoiceId.set(null);
          this.invoiceLoading.set(false);
          this.invoiceFetched.set(true);
        },
      });
  }

  // delete
  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { if (this.deleting()) return; this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.rentalsService.delete(this.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        void this.router.navigate(['/rentals']);
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }
}
